import { useEffect, useState, useRef } from 'react';

/**
 * Cross-tab presence signal for the Mirror surface.
 *
 * Mirror tabs heartbeat a timestamp into `localStorage` every few seconds
 * while mounted. Mission Control tabs read that timestamp to render a
 * subtle "You're also in Mirror" ambient indicator — so a super admin
 * juggling both surfaces never loses context.
 *
 *   useMirrorPresence({ heartbeat: true })   // call from Mirror layout
 *   const { active } = useMirrorPresence()   // call from MC sidebar
 *
 * Implementation:
 *  - Heartbeat: stamp `seerios.mirror.lastSeen = Date.now()` every 4s,
 *    clear on unmount.
 *  - Listener: subscribe to the `storage` event for instant cross-tab
 *    updates + a short interval to expire stale stamps (>10s old).
 */

const KEY = 'seerios.mirror.lastSeen';
const HEARTBEAT_MS = 4000;
const STALE_MS = 10000;
const CHECK_MS = 5000;

function readLastSeen() {
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return 0;
    const n = parseInt(raw, 10);
    return Number.isFinite(n) ? n : 0;
  } catch {
    return 0;
  }
}

function _isActive(now = Date.now()) {
  const last = readLastSeen();
  return last > 0 && now - last < STALE_MS;
}

export default function useMirrorPresence({ heartbeat = false } = {}) {
  const [active, setActive] = useState(() => _isActive());
  const intervalRef = useRef(null);

  // Heartbeat from Mirror tabs.
  useEffect(() => {
    if (!heartbeat) return undefined;
    let cancelled = false;
    const stamp = () => {
      if (cancelled) return;
      try { window.localStorage.setItem(KEY, String(Date.now())); } catch { /* no-op */ }
    };
    stamp();
    const id = window.setInterval(stamp, HEARTBEAT_MS);
    return () => {
      cancelled = true;
      window.clearInterval(id);
      try { window.localStorage.removeItem(KEY); } catch { /* no-op */ }
    };
  }, [heartbeat]);

  // Listener for non-heartbeat consumers (Mission Control).
  useEffect(() => {
    if (heartbeat) return undefined;
    const update = () => setActive(_isActive());
    update();
    const onStorage = (e) => { if (e.key === KEY) update(); };
    window.addEventListener('storage', onStorage);
    intervalRef.current = window.setInterval(update, CHECK_MS);
    return () => {
      window.removeEventListener('storage', onStorage);
      if (intervalRef.current) window.clearInterval(intervalRef.current);
    };
  }, [heartbeat]);

  return { active };
}
